import { useState, useEffect, useRef } from 'react'
import LogoIcon from "../../assets/images/icons/144x.png";
import { navigation } from "../Navigation/navigation";
import { useSelector } from 'react-redux';
import { selectAdminRole, selectPermissions } from "../../slices/staffSlice";
import { Link, useLocation } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { ShieldUserIcon } from '@hugeicons/core-free-icons';

const SIDEBAR_KEY = 'sidebar_expanded';

function NavItem({ item, isActive, expanded }) {
    const linkRef = useRef(null);
    const [tooltipPos, setTooltipPos] = useState(null);

    const showTooltip = () => {
        if (expanded || !linkRef.current) return;
        const r = linkRef.current.getBoundingClientRect();
        setTooltipPos({ top: r.top + r.height / 2, left: r.right + 10 });
    };
    const hideTooltip = () => setTooltipPos(null);

    useEffect(() => {
        if (expanded) setTooltipPos(null);
    }, [expanded]);

    return (
        <li
            className="relative"
            onMouseEnter={showTooltip}
            onMouseLeave={hideTooltip}
        >
            <Link
                ref={linkRef}
                to={item.href}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                        ? 'bg-white/20 text-white'
                        : 'text-white/75 hover:text-white hover:bg-white/10'
                } ${!expanded ? 'justify-center' : ''}`}
            >
                <HugeiconsIcon icon={item.icon} size={20} className="shrink-0" />
                {expanded && <span className="truncate">{item.name}</span>}
            </Link>

            {/* compact tooltip when collapsed — position: fixed so it escapes the scroll container */}
            {!expanded && tooltipPos && (
                <div
                    role="tooltip"
                    style={{ top: tooltipPos.top, left: tooltipPos.left }}
                    className="fixed -translate-y-1/2 z-[100] bg-gray-900 dark:bg-gray-700 text-white rounded-md shadow-lg px-2.5 py-1.5 whitespace-nowrap pointer-events-none text-xs font-medium before:content-[''] before:absolute before:right-full before:top-1/2 before:-translate-y-1/2 before:border-4 before:border-transparent before:border-r-gray-900 dark:before:border-r-gray-700"
                >
                    {item.name}
                </div>
            )}
        </li>
    );
}

function Sidebar() {
    const selectedRole = useSelector(selectAdminRole);
    const permissions = useSelector(selectPermissions);
    const navItems = navigation();
    const location = useLocation();
    const { pathname } = location;
    const path = pathname.split("/");

    const [expanded, setExpanded] = useState(() => {
        const saved = localStorage.getItem(SIDEBAR_KEY);
        return saved === null ? true : saved === 'true';
    });

    useEffect(() => {
        localStorage.setItem(SIDEBAR_KEY, String(expanded));
        if (expanded) {
            document.body.classList.remove("collapsed");
        } else {
            document.body.classList.add("collapsed");
        }
    }, [expanded]);

    useEffect(() => {
        const handler = () => setExpanded(e => !e);
        window.addEventListener('toggle-sidebar', handler);
        return () => window.removeEventListener('toggle-sidebar', handler);
    }, []);

    const hasPermission = (permission) =>
        !permission || permissions.includes('*:*') || permissions.includes(permission);

    const rawLinks = selectedRole === "loan_officer" ? navItems?.loLinks : navItems?.links;
    const staffLinks = rawLinks?.filter(item => hasPermission(item.permission));
    const activeLinks = path[1] === "admin"
        ? navItems?.adminLinks.filter(item => hasPermission(item.permission))
        : staffLinks;

    return (
        <nav
            className={`sidebar-nav hidden md:flex flex-col h-full shrink-0 transition-all duration-200 bg-[#1e3a6e] dark:bg-gray-900 ${expanded ? 'w-64' : 'w-16'}`}
        >
            {/* Logo */}
            <div className={`flex items-center gap-3 px-3 py-4 border-b border-white/10 shrink-0 ${!expanded ? 'justify-center' : ''}`}>
                {expanded ? (
                    <div className="flex items-center gap-2 overflow-hidden">
                        <img src={LogoIcon} className="w-7 h-7 shrink-0" alt="CedisPay" loading="lazy" />
                        <span className="text-white font-semibold text-sm leading-tight truncate">CedisPay</span>
                    </div>
                ) : (
                    <img src={LogoIcon} className="w-7 h-7 shrink-0" alt="CedisPay" loading="lazy" />
                )}
            </div>

            {/* Scrollable nav links */}
            <div
                className="flex-1 py-3 px-2 flex flex-col gap-0.5"
                style={{ overflowY: 'auto', overflowX: 'visible' }}
            >
                <ul className="flex flex-col gap-0.5">
                    {activeLinks?.map((item, index) => (
                        <NavItem
                            key={index}
                            item={item}
                            expanded={expanded}
                            isActive={path[1] === "admin"
                                ? path[2] === item.active.toLowerCase()
                                : path[1] === item.active.toLowerCase()
                            }
                        />
                    ))}

                    {/* Reversals — only when staff has reversal-approve permission */}
                    {hasPermission('transactions:reverse_approve') && selectedRole !== "loan_officer" && path[1] !== "admin" && (
                        <NavItem
                            item={{ name: 'Reversals', href: '/reversals', icon: navItems.reversalsIcon }}
                            expanded={expanded}
                            isActive={path[1] === "reversals"}
                        />
                    )}
                </ul>
            </div>

            {/* Admin / Staff portal toggle — pinned to bottom */}
            {hasPermission('admin:access') && (
                <div className="px-2 pb-3 border-t border-white/10 pt-2 shrink-0">
                    <ul>
                        <NavItem
                            item={{
                                name: path[1] === "admin" ? "Staff Portal" : "Admin Portal",
                                href: path[1] === "admin" ? "/" : "/admin/home",
                                icon: ShieldUserIcon,
                            }}
                            expanded={expanded}
                            isActive={false}
                        />
                    </ul>
                </div>
            )}
        </nav>
    );
}

export default Sidebar;
